/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import { UserWithEmployee } from './UserSelectionModal';
import PerformanceDisciplinaryActionService from '../../../../services/PerformanceDisciplinaryAction';
import PriorDiscussionsTable from './PriorDiscussionsTable';
import { useDisciplinaryActionForm } from './DisciplinaryAction/Form/useDisciplinaryActionForm';
import { INITIAL_FORM_DATA } from './DisciplinaryAction/Form/formConstants';
import {
  UserInfoSection,
  PolicySection,
  ReasonForDisciplinaryActionSection,
  ActionTakenSection,
  ExpectedImprovementSection,
  FollowUpMeetingDateSection,
  PrivateNotesSection,
} from './DisciplinaryAction/Form/FormSections';

interface DisciplinaryActionFormProps {
  isOpen: boolean;
  toggle: () => void;
  selectedUsers?: UserWithEmployee[];
  onSuccess?: () => void;
  editMode?: boolean;
  disciplinaryActionId?: string;
}

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForDisciplinaryAction: string;
  actionTaken: string;
  expectedImprovement: string;
  followUpMeetingDate: string;
  privateNotes: string;
}

const DisciplinaryActionForm = ({
  isOpen,
  toggle,
  selectedUsers = [],
  onSuccess,
  editMode = false,
  disciplinaryActionId,
}: DisciplinaryActionFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const schoolId = currentSchool?.value;
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [submittedForms, setSubmittedForms] = useState<Map<string, FormData>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = selectedUsers[currentUserIndex];
  const isLastUser = currentUserIndex === selectedUsers.length - 1;
  const hasMultipleUsers = !editMode && selectedUsers.length > 1;

  const { formData, errors, policyViolations, priorDiscussions, handleInputChange, validateForm, setFormData } =
    useDisciplinaryActionForm(isOpen, token, currentUser, submittedForms, editMode, disciplinaryActionId);

  useEffect(() => {
    if (!isOpen) {
      setCurrentUserIndex(0);
      setSubmittedForms(new Map());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const saveCurrentForm = () => {
    if (currentUser) {
      const newSubmittedForms = new Map(submittedForms);
      newSubmittedForms.set(currentUser.id, { ...formData });
      setSubmittedForms(newSubmittedForms);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev + 1);
  };

  const handlePrevious = () => {
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent, approved: boolean = true) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(approved);
    } else {
      await submitDirectly(approved);
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(false);
    } else {
      await submitDirectly(false);
    }
  };

  const submitDirectly = async (approved: boolean) => {
    if (!validateForm()) return;
    saveCurrentForm();

    setIsSubmitting(true);
    try {
      const service = PerformanceDisciplinaryActionService(token);
      const finalForms = new Map(submittedForms);
      if (currentUser) {
        finalForms.set(currentUser.id, { ...formData });
      }

      const entries = Array.from(finalForms.entries());
      const disciplinaryActions = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        date: formData.date,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        reasonForDisciplinaryAction: formData.reasonForDisciplinaryAction,
        actionTaken: formData.actionTaken,
        expectedImprovement: formData.expectedImprovement,
        followUpMeetingDate: formData.followUpMeetingDate || undefined,
        privateNotes: formData.privateNotes,
        approved,
      }));

      await service.create({ schoolId, disciplinaryActions });

      toast.success(
        approved
          ? `${
              disciplinaryActions.length === 1
                ? 'Saved as pending for finalization'
                : `${disciplinaryActions.length} Disciplinary Actions saved as pending for finalization`
            }. Download the document, get it signed, then finalize from Pending Finalization.`
          : `${
              disciplinaryActions.length === 1 ? 'Draft saved' : `${disciplinaryActions.length} drafts saved`
            } successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting disciplinary actions:', error);
      toast.error(error?.response?.data?.message || 'Error submitting disciplinary actions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEditForm = async (approved: boolean = true) => {
    if (!disciplinaryActionId) return;

    setIsSubmitting(true);
    try {
      const service = PerformanceDisciplinaryActionService(token);
      const updateData = {
        schoolId,
        date: formData.date,
        policyViolationIds: formData.policyViolationIds,
        reasonForDisciplinaryAction: formData.reasonForDisciplinaryAction,
        actionTaken: formData.actionTaken,
        expectedImprovement: formData.expectedImprovement,
        followUpMeetingDate: formData.followUpMeetingDate || undefined,
        privateNotes: formData.privateNotes,
        approved,
      };

      await service.update(disciplinaryActionId, updateData);

      toast.success(
        approved
          ? 'Disciplinary Action submitted — check Pending Finalization to upload the signed document.'
          : 'Draft saved successfully!',
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error updating disciplinary action:', error);
      toast.error(error?.response?.data?.message || 'Error updating disciplinary action. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCurrentUserIndex(0);
    setSubmittedForms(new Map());
    setFormData(INITIAL_FORM_DATA);
    toggle();
  };

  if (selectedUsers.length === 0 && !editMode) {
    return (
      <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
        <ModalHeader toggle={handleCancel}>Disciplinary Action Notice</ModalHeader>
        <ModalBody>
          <p className="text-muted">Please select at least one employee to create a Disciplinary Action form.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={handleCancel}>
        {editMode ? 'Edit Disciplinary Action Notice' : 'Disciplinary Action Notice'}
        {hasMultipleUsers && (
          <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
            ({currentUserIndex + 1} of {selectedUsers.length})
          </span>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit}>
          <UserInfoSection formData={formData} errors={errors} onChange={handleInputChange} />

          <PriorDiscussionsTable discussions={priorDiscussions} />

          <PolicySection
            formData={formData}
            errors={errors}
            policyViolations={policyViolations}
            onChange={handleInputChange}
          />

          <ReasonForDisciplinaryActionSection
            value={formData.reasonForDisciplinaryAction}
            error={errors.reasonForDisciplinaryAction}
            onChange={(value) => handleInputChange('reasonForDisciplinaryAction', value)}
          />

          <ActionTakenSection
            value={formData.actionTaken}
            error={errors.actionTaken}
            onChange={(value) => handleInputChange('actionTaken', value)}
          />

          <ExpectedImprovementSection
            value={formData.expectedImprovement}
            error={errors.expectedImprovement}
            onChange={(value) => handleInputChange('expectedImprovement', value)}
          />

          <FollowUpMeetingDateSection
            value={formData.followUpMeetingDate}
            onChange={(value) => handleInputChange('followUpMeetingDate', value)}
          />

          <PrivateNotesSection
            value={formData.privateNotes}
            onChange={(value) => handleInputChange('privateNotes', value)}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div className="ml-auto">
          {!editMode && hasMultipleUsers && currentUserIndex > 0 && (
            <Button color="outline-secondary" onClick={handlePrevious} className="mr-2">
              <i className="fas fa-arrow-left mr-2" />
              Previous
            </Button>
          )}
          {!editMode && hasMultipleUsers && !isLastUser ? (
            <Button color="primary" onClick={handleNext}>
              <i className="fas fa-arrow-right mr-2" />
              Next
            </Button>
          ) : (
            <>
              <Button color="warning" onClick={handleSaveDraft} disabled={isSubmitting} className="mr-2">
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" />
                    {hasMultipleUsers ? 'Review & Submit' : 'Submit'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DisciplinaryActionForm;
